// Quality & HACCP data: the daily record of a store (from the sensors'
// series, alarms, close decisions and manual checks), signed day records,
// check records, MKT. Records: app "coldchain", kinds "check" and
// "haccp_day", subject = the store's label value.
import { opsApi, type AlarmRow } from "@gimasi/giothub-shell";
import { tenantApi } from "../lib/account";
import { inRange, REGIMES } from "./classes";
import type { Point } from "./locations";
import { series as readSeries } from "../lib/series";

export interface Row { p: Point; at8: number | null; at17: number | null; min: number | null; max: number | null; mean: number | null; inR: number | null; n: number; alarms: AlarmRow[]; curve: { t: number; v: number }[] }
export interface Rec<B> { id: string; occurred_at: string; author_name: string; version: number; subject_value?: string; body: B }
export interface Check { kind: "opening" | "goods" | "closing"; ok: boolean; probe?: string; note?: string }
export interface DaySign { date: string; signer: string; meaning: string; sha256: string; points: number }

const KEYS = ["ext_temperature", "temperature_1"];
async function series(id: string, from: number, to: number, res: "raw" | "1h" = "raw") {
  for (const key of KEYS) {
    try { const s = await readSeries(id, { key, from: new Date(from).toISOString(), to: new Date(to).toISOString(), resolution: res }); if (s.items.length) return s.items; } catch { /* next key */ }
  }
  return [];
}
const near = (xs: { utc: string; value: number }[], t: number) => { let best: number | null = null, bd = 30 * 60e3; for (const x of xs) { const d = Math.abs(Date.parse(x.utc) - t); if (d < bd) { bd = d; best = x.value; } } return best; };

export async function dayRows(points: Point[], from: number, to: number): Promise<Row[]> {
  const al = await opsApi.alarms({ from: new Date(from).toISOString(), limit: 500 }).catch(() => ({ items: [] as AlarmRow[] }));
  return Promise.all(points.map(async (p) => {
    const xs = (await series(p.id, from, to)).filter((x) => Date.parse(x.utc) >= from && Date.parse(x.utc) <= to);
    const vs = xs.map((x) => x.value);
    return {
      p, n: vs.length, at8: near(xs, from + 8 * 3600e3), at17: near(xs, from + 17 * 3600e3),
      min: vs.length ? Math.min(...vs) : null, max: vs.length ? Math.max(...vs) : null, mean: vs.length ? vs.reduce((a, b) => a + b, 0) / vs.length : null,
      inR: vs.length ? (vs.filter((v) => inRange(p.regime, v)).length / vs.length) * 100 : null,
      alarms: (al.items as AlarmRow[]).filter((a) => a.device_id === p.id && Date.parse(a.utc) < to && a.raised !== false),
      curve: xs.map((x) => ({ t: Date.parse(x.utc), v: x.value })),
    };
  }));
}

export const records = <B,>(kind: string, site: string, from: number, to: number) =>
  tenantApi<{ items: Rec<B>[] }>("GET", `/records?app=coldchain&kind=${kind}&subject_key=site&subject_value=${encodeURIComponent(site)}&from=${new Date(from).toISOString()}&before=${new Date(to).toISOString()}&limit=200`).then((r) => r.items, () => [] as Rec<B>[]);
export const addRecord = (kind: string, site: string, title: string, body: unknown, status?: string) =>
  tenantApi("POST", "/records", { app: "coldchain", kind, subject_key: "site", subject_value: site, title, body, ...(status ? { status } : {}) });

export async function sha256(text: string) {
  const b = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(b)].map((x) => x.toString(16).padStart(2, "0")).join("");
}

/** mean kinetic temperature (ΔH = 83.144 kJ/mol, R = 8.3144 J/mol K), °C */
export function mkt(vs: number[]) {
  if (!vs.length) return null;
  const k = 83144 / 8.3144;
  const s = vs.reduce((a, v) => a + Math.exp(-k / (v + 273.15)), 0) / vs.length;
  return k / -Math.log(s) - 273.15;
}
export async function monthStats(points: Point[]) {
  const to = Date.now(), from = to - 30 * 864e5;
  return Promise.all(points.slice(0, 40).map(async (p) => {
    const vs = (await series(p.id, from, to, "1h")).map((x) => x.value);
    const c = REGIMES[p.regime];
    return { p, n: vs.length, mean: vs.length ? vs.reduce((a, b) => a + b, 0) / vs.length : null, min: vs.length ? Math.min(...vs) : null, max: vs.length ? Math.max(...vs) : null,
      inR: vs.length ? (vs.filter((v) => inRange(p.regime, v)).length / vs.length) * 100 : null, mkt: mkt(vs), limit: c?.warn ?? null };
  }));
}

export function csv(name: string, rows: (string | number | null | undefined)[][]) {
  const text = rows.map((r) => r.map((x) => { const s = x == null ? "" : String(x); return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; }).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  a.download = name; a.click();
}
